import React, { useId } from 'react';
import { RefreshCw, X } from 'lucide-react';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Button } from '@/components/ui/button';
import { Separator } from '@/components/ui/separator';
import { Spinner } from '@/components/ui/spinner';
import { CADModel } from '../../types/cad';
import { LinearLoader } from './thickness/atoms';
import { ThicknessConditionsForm } from './thickness/ThicknessConditionsForm';
import { ThicknessHistoryControls } from './thickness/ThicknessHistoryControls';
import { resolveApiErrorMessage } from '../../utils/apiErrorMessages';
import { ThicknessResult } from './thickness/ThicknessResult';
import { useCADThicknessPanel } from './thickness/useCADThicknessPanel';
import type { ThicknessViewState } from './thickness/helpers';

export type { ThicknessDisplayMode, ThicknessViewState } from './thickness/helpers';

interface CADThicknessPanelProps {
  model: CADModel | null;
  canAnalyze: boolean;
  canApprove?: boolean;
  requestedAnalysisId: string | null;
  requestedRegionId: string | null;
  refreshKey: number;
  /** The run the workspace already shows; if it is a completed run of `model`, it stays on screen across the model/refresh reset instead of falling back to the base view. */
  seedAnalysis?: ThicknessViewState['analysis'];
  /** True while the workspace's own URL write is still committing. */
  urlWritePending?: boolean;
  /** Records the run in the URL; must also clear `?region=` (the panel writes once per run switch). */
  onAnalysisChange: (analysisId: string | null) => void;
  onRegionChange: (regionId: string | null) => void;
  onViewChange: (state: ThicknessViewState) => void;
}

const CADThicknessPanel: React.FC<CADThicknessPanelProps> = ({
  model,
  canAnalyze,
  canApprove = false,
  requestedAnalysisId,
  requestedRegionId,
  refreshKey,
  seedAnalysis = null,
  urlWritePending = false,
  onAnalysisChange,
  onRegionChange,
  onViewChange,
}) => {
  const uid = useId();
  const ctrl = useCADThicknessPanel({
    model,
    requestedAnalysisId,
    requestedRegionId,
    refreshKey,
    seedAnalysis,
    urlWritePending,
    onAnalysisChange,
    onRegionChange,
    onViewChange,
  });
  const { t } = ctrl;

  if (!model) {
    return (
      <div className="p-4">
        <Alert variant="info">
          <AlertDescription>{t('cad.thickness.selectModel')}</AlertDescription>
        </Alert>
      </div>
    );
  }

  if (model.status !== 'completed') {
    return (
      <div className="p-4">
        <Alert variant={model.status === 'failed' ? 'destructive' : 'info'}>
          <AlertDescription>
            {model.status === 'failed'
              ? resolveApiErrorMessage(t, { code: model.errorCode }, {
                fallbackKey: 'cad.thickness.modelFailed',
              })
              : t('cad.thickness.modelProcessing')}
          </AlertDescription>
        </Alert>
      </div>
    );
  }

  return (
    <div className="p-3">
      {ctrl.error && (
        <Alert variant="destructive" className="mb-3 flex items-start justify-between gap-2">
          <AlertDescription>{ctrl.error}</AlertDescription>
          <button
            type="button"
            onClick={() => ctrl.setError(null)}
            aria-label={t('common.close', 'Close')}
            className="-mr-1 -mt-0.5 shrink-0 rounded-sm text-destructive/80 hover:text-destructive"
          >
            <X className="size-4" />
          </button>
        </Alert>
      )}
      {ctrl.analysisListError && (
        <Alert variant="destructive" className="mb-3 flex items-start justify-between gap-2">
          <div className="min-w-0">
            <AlertDescription>{ctrl.analysisListError}</AlertDescription>
            {ctrl.retryAfterSeconds ? (
              <p className="mt-1 text-xs">
                {t('cad.thickness.retryHint', { seconds: ctrl.retryAfterSeconds })}
              </p>
            ) : null}
          </div>
          <div className="flex shrink-0 items-center gap-1">
            <Button
              variant="ghost"
              size="sm"
              onClick={ctrl.retryAnalysisList}
              disabled={ctrl.loading}
              className="h-7 gap-1 px-2 text-destructive hover:text-destructive"
            >
              {ctrl.loading
                ? <Spinner className="size-3.5" />
                : <RefreshCw aria-hidden="true" className="size-3.5" />}
              {t('cad.thickness.retryLoad')}
            </Button>
            <button
              type="button"
              onClick={() => ctrl.setAnalysisListError(null)}
              aria-label={t('common.close', 'Close')}
              className="-mr-1 -mt-0.5 shrink-0 rounded-sm text-destructive/80 hover:text-destructive"
            >
              <X className="size-4" />
            </button>
          </div>
        </Alert>
      )}
      {ctrl.invalidRequestedAnalysisId && (
        <Alert variant="destructive" className="mb-3">
          <AlertDescription>
            {t('cad.thickness.analysisNotFound', {
              analysisId: ctrl.invalidRequestedAnalysisId,
            })}
          </AlertDescription>
        </Alert>
      )}
      {ctrl.regionLinkError && (
        <Alert variant="warning" className="mb-3 flex items-start justify-between gap-2">
          <AlertDescription>{ctrl.regionLinkError}</AlertDescription>
          <button
            type="button"
            onClick={() => ctrl.setRegionLinkError(null)}
            aria-label={t('common.close', 'Close')}
            className="-mr-1 -mt-0.5 shrink-0 rounded-sm text-foreground/70 hover:text-foreground"
          >
            <X className="size-4" />
          </button>
        </Alert>
      )}

      <ThicknessConditionsForm ctrl={ctrl} uid={uid} canAnalyze={canAnalyze} />

      <Separator className="my-3" />
      <ThicknessHistoryControls ctrl={ctrl} uid={uid} canAnalyze={canAnalyze} />

      {ctrl.loading && <LinearLoader />}

      <ThicknessResult
        ctrl={ctrl}
        uid={uid}
        canAnalyze={canAnalyze}
        canApprove={canApprove}
      />
    </div>
  );
};

export default CADThicknessPanel;
